import { useState, useEffect, useCallback } from 'react';
import { contactService, ContactInfo } from '../services/contactService';
import { CONTACT_WHATSAPP } from '../config/brand';

// Cache for contact information to avoid repeated API calls
let contactCache: ContactInfo | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Function to clear contact cache (useful for logout)
export const clearContactCache = () => {
  contactCache = null;
  cacheTimestamp = 0;
  contactService.clearGetAllPromise();
};

export const useContact = () => {
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if cache is still valid
  const isCacheValid = useCallback(() => {
    return contactCache && (Date.now() - cacheTimestamp) < CACHE_DURATION;
  }, []);

  // Load contact information
  const loadContactInfo = useCallback(async (forceRefresh = false) => {
    // Use cache if valid and not forcing refresh
    if (!forceRefresh && isCacheValid()) {
      setContactInfo(contactCache);
      return contactCache;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await contactService.getAll();
      if (response.success && response.data.length > 0) {
        const contact = response.data[0];

        // Update cache
        contactCache = contact;
        cacheTimestamp = Date.now();

        setContactInfo(contact);
        return contact;
      } else {
        setContactInfo(null);
        return null;
      }
    } catch (err: any) {
      // Silently handle 404 errors (endpoint may not exist on all deployments)
      // Only log non-404 errors
      if (err.response && err.response.status === 404) {
        // Use fallback data - no need to log expected 404
        setError(null);
      } else {
        const errorMessage = err.message || 'Failed to load contact information';
        setError(errorMessage);
        console.warn('Contact info unavailable:', errorMessage);
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [isCacheValid]);

  // Load contact info on mount
  useEffect(() => {
    loadContactInfo();
  }, [loadContactInfo]);

  // Always use brand WhatsApp 01023483013 (201023483013)
  const getWhatsAppNumber = useCallback((): string => {
    return CONTACT_WHATSAPP;
  }, []);

  // Get WhatsApp URL for a specific message
  const getWhatsAppUrl = useCallback(
    (message: string = 'مرحباً'): string => {
      return `https://wa.me/${CONTACT_WHATSAPP}?text=${encodeURIComponent(message)}`;
    },
    []
  );

  // Open WhatsApp with message
  const openWhatsApp = useCallback(
    (message: string = 'مرحباً') => {
      window.open(getWhatsAppUrl(message), '_blank', 'noopener,noreferrer');
    },
    [getWhatsAppUrl]
  );

  return {
    contactInfo,
    isLoading,
    error,
    loadContactInfo,
    getWhatsAppNumber,
    getWhatsAppUrl,
    openWhatsApp,
    // Convenience getters
    whatsappNumber: CONTACT_WHATSAPP,
    facebookPage: contactInfo?.facebook_Page || null,
    youtubeChannel: contactInfo?.youTube_Channel || null,
    tiktokChannel: contactInfo?.tiktokChannel || null,
  };
};

export default useContact;
